import React, { useCallback, useMemo, useRef, useState } from 'react'
import Papa from 'papaparse'
import {
  Alert,
  Button,
  Card,
  Progress,
  Space,
  Table,
  Tag,
  Typography,
} from '@surecompanies/core_components'
import { Select, notification } from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  UploadOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { downloadJSONasCSV } from 'helpers/download-file'
import {
  BatchUpdateBenefitResponseDto,
  UpdateBenefitRequestDto,
} from 'modules/benefits/dtos'
import { batchUpdateBenefits } from 'modules/benefits/methods'
import { PaymentTypeEnum } from 'types/benefit'

import {
  findDuplicateBenefitIds,
  findMissingHeaders,
  getRowErrors,
  isValidRow,
  mapChunkError,
  normalizePaymentMethod,
  ParsedRow,
  REQUIRED_CSV_HEADERS,
} from './PaymentBatchUpdateUpload.helpers'
import useFeatureFlags from 'hooks/useFeatureFlags'

const { Title, Text } = Typography

type Step = 'upload' | 'preview' | 'results'

// ---------------------------------------------------------------------------
// CSV template (required columns only; backend defaults ACH type/use)
// ---------------------------------------------------------------------------

type ExampleRow = Record<(typeof REQUIRED_CSV_HEADERS)[number], string>

const BASE_EXAMPLE_ROWS: ExampleRow[] = [
  {
    benefitId: 'benefit-ach-example-id',
    carrierApplicationPrimaryPaymentMethod: PaymentTypeEnum.ACH,
    carrierApplicationPrimaryACHAccountNumber: '123456789',
  },
  {
    benefitId: 'benefit-vcn-example-id',
    carrierApplicationPrimaryPaymentMethod: PaymentTypeEnum.VCN,
    carrierApplicationPrimaryACHAccountNumber: '',
  },
]

const CHECK_EXAMPLE_ROW: ExampleRow = {
  benefitId: 'benefit-check-example-id',
  carrierApplicationPrimaryPaymentMethod: PaymentTypeEnum.CHECK,
  carrierApplicationPrimaryACHAccountNumber: '',
}

const GROUP_ACH_EXAMPLE_ROW: ExampleRow = {
  benefitId: 'benefit-group-ach-example-id',
  carrierApplicationPrimaryPaymentMethod: PaymentTypeEnum.GROUP_ACH,
  carrierApplicationPrimaryACHAccountNumber: '',
}

const CHUNK_SIZE = 250
const MAX_CSV_ROWS = 20000

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function PaymentBatchUpdateUpload() {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [step, setStep] = useState<Step>('upload')

  const { flags } = useFeatureFlags()
  const enableCheck = flags['admin.finance.payment-coordination-enable-check']
  const enableGroupAch =
    flags['admin.finance.payment-coordination-enable-group-ach']

  // Build the CSV template rows and the payment method picker options based on
  // the feature flags so disabled methods are hidden everywhere at once.
  const exampleRows: ExampleRow[] = [
    ...BASE_EXAMPLE_ROWS,
    ...(enableCheck ? [CHECK_EXAMPLE_ROW] : []),
    ...(enableGroupAch ? [GROUP_ACH_EXAMPLE_ROW] : []),
  ]

  const paymentMethodOptions = [
    { value: PaymentTypeEnum.ACH, label: PaymentTypeEnum.ACH },
    { value: PaymentTypeEnum.VCN, label: PaymentTypeEnum.VCN },
    ...(enableCheck
      ? [{ value: PaymentTypeEnum.CHECK, label: PaymentTypeEnum.CHECK }]
      : []),
    ...(enableGroupAch
      ? [
          {
            value: PaymentTypeEnum.GROUP_ACH,
            label: PaymentTypeEnum.GROUP_ACH,
          },
        ]
      : []),
  ]

  // Description text for the CSV format card (method list adapts to flags).
  // Used in two places:
  //  1. "required: <methodListAll>" — all enabled methods for the format hint
  //  2. "<noAchMethodList> do not require an ACH account number" — non-ACH only
  const methodListAll = [
    'ACH',
    'VCN',
    ...(enableCheck ? ['Check'] : []),
    ...(enableGroupAch ? ['GROUP ACH'] : []),
  ].join(', ')

  const noAchMethodList = [
    'VCN',
    ...(enableCheck ? ['Check'] : []),
    ...(enableGroupAch ? ['GROUP ACH'] : []),
  ].join(', ')
  const [rows, setRows] = useState<ParsedRow[]>([])
  const [parseError, setParseError] = useState<string | null>(null)
  const [results, setResults] = useState<BatchUpdateBenefitResponseDto | null>(
    null
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [showAllErrors, setShowAllErrors] = useState(false)
  const cancelledRef = useRef(false)
  const [batchProgress, setBatchProgress] = useState({
    sent: 0,
    total: 0,
    currentBatch: 0,
    totalBatches: 0,
  })

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleDownloadTemplate = () => {
    downloadJSONasCSV(exampleRows, 'payment-batch-update-template.csv')
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setParseError(null)

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        if (result.errors.length > 0) {
          setParseError(
            `CSV parse error: ${result.errors.map((err) => err.message).join(', ')}`
          )
          return
        }

        const missingHeaders = findMissingHeaders(
          result.meta.fields ?? [],
          REQUIRED_CSV_HEADERS
        )
        if (missingHeaders.length > 0) {
          setParseError(
            `CSV is missing required column(s): ${missingHeaders.join(', ')}. Download the template to see the expected format.`
          )
          return
        }

        const parsed: ParsedRow[] = result.data.map((rawRow, index) => ({
          key: String(index),
          benefitId: rawRow['benefitId']?.trim() ?? '',
          carrierApplicationPrimaryPaymentMethod: normalizePaymentMethod(
            rawRow['carrierApplicationPrimaryPaymentMethod'] ?? ''
          ),
          carrierApplicationPrimaryACHAccountNumber:
            rawRow['carrierApplicationPrimaryACHAccountNumber']?.trim() ?? '',
        }))

        if (parsed.length === 0) {
          setParseError('CSV has no data rows.')
          return
        }
        if (parsed.length > MAX_CSV_ROWS) {
          setParseError(
            `CSV contains ${parsed.length} rows. Maximum allowed is ${MAX_CSV_ROWS.toLocaleString()}.`
          )
          return
        }

        setRows(parsed)
        setStep('preview')
      },
      error: (err: Error) => {
        setParseError(`Failed to read file: ${err.message}`)
      },
    })

    // Reset input so the same file can be re-selected after fixing
    e.target.value = ''
  }

  const handleCellChange = (
    key: string,
    field: keyof ParsedRow,
    value: string
  ) => {
    setRows((curr) =>
      curr.map((r) => {
        if (r.key !== key) return r
        const updated = { ...r, [field]: value }
        if (
          field === 'carrierApplicationPrimaryPaymentMethod' &&
          value !== PaymentTypeEnum.ACH
        ) {
          updated.carrierApplicationPrimaryACHAccountNumber = ''
        }
        return updated
      })
    )
  }

  const handleConfirm = useCallback(async () => {
    // Recompute here (not via the render memo) so the closure can't go stale.
    const duplicateIds = findDuplicateBenefitIds(rows)
    const isSubmittable = (row: ParsedRow): boolean =>
      isValidRow(row) && !duplicateIds.has(row.benefitId.trim())
    const validRows = rows.filter(isSubmittable)
    // Surface rows skipped by client-side validation (or as duplicates) so they
    // aren't silently dropped and reported as an all-success run.
    const skippedErrors: BatchUpdateBenefitResponseDto['errors'] = rows
      .filter((row) => !isSubmittable(row))
      .map((row) => {
        const id = row.benefitId.trim()
        const issues = getRowErrors(row)
        if (id && duplicateIds.has(id)) {
          issues.push('Duplicate benefit ID in file (row not submitted)')
        }
        return {
          benefitId: id || '(missing benefit ID)',
          message: issues.join(', '),
        }
      })
    const items = validRows.map((row) => {
      const method = row.carrierApplicationPrimaryPaymentMethod
      const data: UpdateBenefitRequestDto = {
        carrierApplicationPrimaryPaymentMethod:
          method === PaymentTypeEnum.ACH ||
          method === PaymentTypeEnum.VCN ||
          method === PaymentTypeEnum.CHECK ||
          method === PaymentTypeEnum.GROUP_ACH
            ? method
            : undefined,
      }
      if (method === PaymentTypeEnum.ACH) {
        data.carrierApplicationPrimaryACHAccountNumber =
          row.carrierApplicationPrimaryACHAccountNumber || undefined
      } else if (
        method === PaymentTypeEnum.VCN ||
        method === PaymentTypeEnum.CHECK ||
        method === PaymentTypeEnum.GROUP_ACH
      ) {
        data.carrierApplicationPrimaryACHAccountNumber = null
        data.carrierApplicationPrimaryACHAccountType = null
        data.carrierApplicationPrimaryACHAccountUse = null
      }
      return { benefitId: row.benefitId, data }
    })

    const totalBatches = Math.ceil(items.length / CHUNK_SIZE)

    cancelledRef.current = false
    setIsCancelling(false)
    setIsSubmitting(true)
    setBatchProgress({
      sent: 0,
      total: items.length,
      currentBatch: 1,
      totalBatches,
    })

    const allResults: string[] = []
    const allErrors: BatchUpdateBenefitResponseDto['errors'] = [
      ...skippedErrors,
    ]

    let submittedCount = 0
    for (let i = 0; i < items.length; i += CHUNK_SIZE) {
      if (cancelledRef.current) break

      const batchNum = Math.floor(i / CHUNK_SIZE) + 1
      setBatchProgress((prev) => ({ ...prev, currentBatch: batchNum }))

      const chunk = items.slice(i, i + CHUNK_SIZE)
      try {
        const response = await batchUpdateBenefits({ items: chunk })
        allResults.push(...response.results)
        allErrors.push(...response.errors)
      } catch (error) {
        allErrors.push(...mapChunkError(chunk, error))
      }
      submittedCount = Math.min(i + CHUNK_SIZE, items.length)
      setBatchProgress((prev) => ({ ...prev, sent: submittedCount }))
    }

    // Report valid rows that were never sent because the user cancelled, so the
    // results reflect the true outcome instead of implying the batch finished.
    if (cancelledRef.current && submittedCount < items.length) {
      allErrors.push(
        ...items.slice(submittedCount).map((item) => ({
          benefitId: item.benefitId || '(missing benefit ID)',
          message: 'Not submitted — upload cancelled',
        }))
      )
    }

    if (
      cancelledRef.current &&
      allResults.length === 0 &&
      allErrors.length === 0
    ) {
      setIsSubmitting(false)
      return
    }

    setIsSubmitting(false)
    setResults({ results: allResults, errors: allErrors })
    setStep('results')

    if (allResults.length > 0) {
      notification.success({
        message: `Successfully updated ${allResults.length} benefit(s)`,
      })
    }
    if (allErrors.length > 0) {
      notification.warning({
        message: `${allErrors.length} row(s) skipped or failed`,
      })
    }
  }, [rows])

  const handleDownloadFailedRows = () => {
    if (!results) return
    const rowByBenefitId = new Map(rows.map((r) => [r.benefitId.trim(), r]))
    const data = results.errors.map(({ benefitId, message }) => {
      const original = rowByBenefitId.get(benefitId)
      return {
        benefitId,
        carrierApplicationPrimaryPaymentMethod:
          original?.carrierApplicationPrimaryPaymentMethod ?? '',
        carrierApplicationPrimaryACHAccountNumber:
          original?.carrierApplicationPrimaryACHAccountNumber ?? '',
        error: message,
      }
    })
    downloadJSONasCSV(data, 'payment-batch-update-failed.csv')
  }

  const handleDownloadFullResults = () => {
    if (!results) return
    const successSet = new Set(results.results)
    const errorMap = new Map(
      results.errors.map(({ benefitId, message }) => [benefitId, message])
    )
    const data = rows.map((row) => {
      const id = row.benefitId.trim()
      const errorMsg = errorMap.get(id)
      const succeeded = successSet.has(id)
      return {
        benefitId: id,
        carrierApplicationPrimaryPaymentMethod:
          row.carrierApplicationPrimaryPaymentMethod,
        carrierApplicationPrimaryACHAccountNumber:
          row.carrierApplicationPrimaryACHAccountNumber,
        status: errorMsg ? 'failed' : succeeded ? 'success' : 'skipped',
        error: errorMsg ?? '',
      }
    })
    downloadJSONasCSV(data, 'payment-batch-update-results.csv')
  }

  const handleReset = () => {
    cancelledRef.current = true
    setIsSubmitting(false)
    setIsCancelling(false)
    setShowAllErrors(false)
    setStep('upload')
    setRows([])
    setResults(null)
    setParseError(null)
    setBatchProgress({ sent: 0, total: 0, currentBatch: 0, totalBatches: 0 })
  }

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  // Benefit IDs duplicated across rows — the backend doesn't dedupe and would
  // race concurrent writes, so we flag them here and skip them at submit.
  const duplicateBenefitIds = useMemo(
    () => findDuplicateBenefitIds(rows),
    [rows]
  )

  // All reasons a row won't be submitted: field validation + duplicate ID.
  const getRowIssues = useCallback(
    (row: ParsedRow): string[] => {
      const issues = getRowErrors(row)
      const id = row.benefitId.trim()
      if (id && duplicateBenefitIds.has(id)) {
        issues.push('Duplicate benefit ID in file')
      }
      return issues
    },
    [duplicateBenefitIds]
  )

  const validCount = useMemo(
    () => rows.filter((row) => getRowIssues(row).length === 0).length,
    [rows, getRowIssues]
  )
  const invalidCount = rows.length - validCount

  // Concrete per-row reasons so the user sees what's wrong at the top, not just a count.
  const invalidRowSummaries = useMemo(
    () =>
      rows
        .map((row, index) => ({ row, index, errors: getRowIssues(row) }))
        .filter((entry) => entry.errors.length > 0),
    [rows, getRowIssues]
  )

  const MAX_LISTED_ERRORS = 2

  // Float invalid rows to the top so errors are visible on the first page,
  // keeping the original CSV order within each group (stable).
  const sortedRows = useMemo(
    () =>
      rows
        .map((row, index) => ({
          row,
          index,
          valid: getRowIssues(row).length === 0,
        }))
        .sort((a, b) =>
          a.valid === b.valid ? a.index - b.index : a.valid ? 1 : -1
        )
        .map((entry) => entry.row),
    [rows, getRowIssues]
  )

  // Original CSV row number (1-based) keyed by row, so the "#" column matches the
  // banner's "Row N" even though the table floats invalid rows to the top.
  const rowNumberByKey = useMemo(
    () => new Map(rows.map((row, index) => [row.key, index + 1])),
    [rows]
  )

  const previewColumns = [
    {
      title: '#',
      key: 'rowNumber',
      width: 56,
      fixed: 'left' as const,
      render: (_: unknown, row: ParsedRow) => (
        <Text type="secondary">{rowNumberByKey.get(row.key)}</Text>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 60,
      fixed: 'left' as const,
      render: (_: unknown, row: ParsedRow) => {
        const errors = getRowIssues(row)
        return errors.length === 0 ? (
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
        ) : (
          <CloseCircleOutlined
            style={{ color: '#ff4d4f', fontSize: 16 }}
            title={errors.join('\n')}
          />
        )
      },
    },
    {
      title: 'Issues',
      key: 'issues',
      width: 240,
      fixed: 'left' as const,
      render: (_: unknown, row: ParsedRow) => {
        const errors = getRowIssues(row)
        if (errors.length === 0) {
          return <Text type="secondary">—</Text>
        }
        return (
          <ul
            style={{
              margin: 0,
              paddingLeft: 16,
              color: '#ff4d4f',
              fontSize: 12,
            }}
          >
            {errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        )
      },
    },
    {
      title: 'Benefit ID',
      dataIndex: 'benefitId',
      key: 'benefitId',
      width: 220,
      render: (value: string, row: ParsedRow) => (
        <input
          style={{
            border: '1px solid #d9d9d9',
            borderRadius: 4,
            padding: '2px 6px',
            width: '100%',
            fontSize: 13,
          }}
          value={value}
          onChange={(e) =>
            handleCellChange(row.key, 'benefitId', e.target.value)
          }
        />
      ),
    },
    {
      title: 'Payment Method',
      dataIndex: 'carrierApplicationPrimaryPaymentMethod',
      key: 'paymentMethod',
      width: 130,
      render: (value: string, row: ParsedRow) => (
        <Select
          size="small"
          style={{ width: '100%' }}
          value={value || undefined}
          placeholder="Select"
          onChange={(v: string) =>
            handleCellChange(
              row.key,
              'carrierApplicationPrimaryPaymentMethod',
              v
            )
          }
          options={paymentMethodOptions}
        />
      ),
    },
    {
      title: 'ACH Account Number',
      dataIndex: 'carrierApplicationPrimaryACHAccountNumber',
      key: 'achAccountNumber',
      width: 160,
      render: (value: string, row: ParsedRow) => (
        <input
          style={{
            border: '1px solid #d9d9d9',
            borderRadius: 4,
            padding: '2px 6px',
            width: '100%',
            fontSize: 13,
          }}
          value={value}
          disabled={
            row.carrierApplicationPrimaryPaymentMethod !== PaymentTypeEnum.ACH
          }
          onChange={(e) =>
            handleCellChange(
              row.key,
              'carrierApplicationPrimaryACHAccountNumber',
              e.target.value
            )
          }
        />
      ),
    },
  ]

  // -------------------------------------------------------------------------
  // Upload step
  // -------------------------------------------------------------------------

  if (step === 'upload') {
    return (
      <Card style={{ padding: '24px' }}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Title level={4} style={{ margin: 0 }}>
            Primary Payment Method Batch Update
          </Title>
          <Text type="secondary">
            Upload a CSV file to update the carrier application primary payment
            method for multiple benefits at once. Each row can have a different
            payment method and ACH account number. ACH rows always use Checking
            account type and Personal account use (applied by the server).{' '}
            {noAchMethodList} do not require an ACH account number.
          </Text>

          {parseError && (
            <Alert type="error" message={parseError} showIcon closable />
          )}

          <Space wrap>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
            >
              Download CSV Template
            </Button>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload CSV
            </Button>
          </Space>

          <Card
            size="small"
            style={{ background: '#fafafa', borderStyle: 'dashed' }}
          >
            <Text strong>CSV format — required columns:</Text>
            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
              {REQUIRED_CSV_HEADERS.map((h) => (
                <li key={h}>
                  <Text code>{h}</Text>
                  {h === 'benefitId' && (
                    <Text type="secondary"> — required</Text>
                  )}
                  {h === 'carrierApplicationPrimaryPaymentMethod' && (
                    <Text type="secondary"> — required: {methodListAll}</Text>
                  )}
                  {h === 'carrierApplicationPrimaryACHAccountNumber' && (
                    <Text type="secondary"> — required when method is ACH</Text>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        </Space>
      </Card>
    )
  }

  // -------------------------------------------------------------------------
  // Preview step
  // -------------------------------------------------------------------------

  if (step === 'preview') {
    return (
      <Card style={{ padding: '24px' }}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Space style={{ justifyContent: 'space-between', width: '100%' }}>
            <Title level={4} style={{ margin: 0 }}>
              Preview &amp; Edit
            </Title>
            <Button onClick={handleReset} disabled={isSubmitting}>
              Back to Upload
            </Button>
          </Space>

          <Space>
            <Tag color="success">
              <CheckCircleOutlined /> {validCount} valid
            </Tag>
            {invalidCount > 0 && (
              <Tag color="error">
                <CloseCircleOutlined /> {invalidCount} invalid (will be skipped)
              </Tag>
            )}
          </Space>

          {invalidCount > 0 && (
            <Alert
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              message={
                <div>
                  <div>
                    {`${invalidCount} row(s) have validation errors or duplicate benefit IDs and will be skipped. Fix the highlighted rows (shown first) or they will not be submitted.`}
                  </div>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {(showAllErrors
                      ? invalidRowSummaries
                      : invalidRowSummaries.slice(0, MAX_LISTED_ERRORS)
                    ).map(({ row, index, errors }) => (
                      <li key={row.key}>
                        <Text strong>Row {index + 1}</Text>
                        {row.benefitId ? ` (${row.benefitId})` : ''}:{' '}
                        {errors.join(', ')}
                      </li>
                    ))}
                  </ul>
                  {invalidRowSummaries.length > MAX_LISTED_ERRORS && (
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0, height: 'auto' }}
                      onClick={() => setShowAllErrors((curr) => !curr)}
                    >
                      {showAllErrors
                        ? 'Show less'
                        : `Show all ${invalidRowSummaries.length} errors`}
                    </Button>
                  )}
                </div>
              }
            />
          )}

          {validCount === 0 && (
            <Alert
              type="error"
              showIcon
              message="No valid rows to submit. Please fix the errors above."
            />
          )}

          <Table
            dataSource={sortedRows}
            columns={previewColumns}
            rowKey="key"
            size="small"
            scroll={{ x: 950 }}
            pagination={{ pageSize: 50, showSizeChanger: false }}
            rowClassName={(row) =>
              getRowIssues(row).length === 0 ? '' : 'ant-table-row-error'
            }
          />

          <style>{`
            .ant-table-row-error td {
              background-color: #fff2f0 !important;
            }
            .ant-pagination-item,
            .ant-pagination-prev,
            .ant-pagination-next {
              margin-inline-end: 6px !important;
            }
          `}</style>

          {isSubmitting && batchProgress.total > 0 && (
            <>
              <Text type="secondary">
                {isCancelling
                  ? 'Cancelling… waiting for current batch to finish'
                  : `Sending batch ${batchProgress.currentBatch} of ${batchProgress.totalBatches} (${CHUNK_SIZE} items per batch)…`}
              </Text>
              <Progress
                percent={Math.round(
                  (batchProgress.sent / batchProgress.total) * 100
                )}
                format={() =>
                  `${batchProgress.sent} / ${batchProgress.total} rows`
                }
                status={isCancelling ? 'exception' : 'active'}
              />
            </>
          )}

          {invalidCount > 0 && !isSubmitting && (
            <Alert
              type="info"
              showIcon
              message="You can submit as-is — valid rows will save and you'll be able to download a CSV of the failed rows with reasons after submission."
            />
          )}

          <Space>
            {isSubmitting ? (
              <Button
                danger
                disabled={isCancelling}
                onClick={() => {
                  cancelledRef.current = true
                  setIsCancelling(true)
                }}
              >
                {isCancelling ? 'Cancelling…' : 'Cancel Upload'}
              </Button>
            ) : (
              <Button
                type="primary"
                disabled={validCount === 0}
                onClick={handleConfirm}
              >
                Confirm &amp; Submit {validCount} Benefit(s)
              </Button>
            )}
            <Button onClick={handleReset} disabled={isSubmitting}>
              Cancel
            </Button>
          </Space>
        </Space>
      </Card>
    )
  }

  // -------------------------------------------------------------------------
  // Results step
  // -------------------------------------------------------------------------

  const successColumns = [
    {
      title: 'Benefit ID',
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => <Text code>{id}</Text>,
    },
  ]

  const errorColumns = [
    { title: 'Benefit ID', dataIndex: 'benefitId', key: 'benefitId' },
    { title: 'Error', dataIndex: 'message', key: 'message' },
  ]

  return (
    <Card style={{ padding: '24px' }}>
      <Space direction="vertical" size={24} style={{ width: '100%' }}>
        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
          <Title level={4} style={{ margin: 0 }}>
            Batch Update Results
          </Title>
          <Space wrap>
            {results && results.errors.length > 0 && (
              <Button
                icon={<DownloadOutlined />}
                onClick={handleDownloadFailedRows}
              >
                Download failed rows
              </Button>
            )}
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadFullResults}
            >
              Download full results
            </Button>
            <Button type="primary" onClick={handleReset}>
              Start New Batch
            </Button>
          </Space>
        </Space>

        {results && results.results.length > 0 && (
          <Alert
            type="success"
            showIcon
            message={`${results.results.length} benefit(s) updated successfully`}
          />
        )}

        {results && results.errors.length > 0 && (
          <Alert
            type="error"
            showIcon
            message={`${results.errors.length} row(s) skipped or failed (see details below)`}
          />
        )}

        {results && results.results.length > 0 && (
          <Card
            size="small"
            title={
              <Text strong>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />{' '}
                Successfully Updated ({results.results.length})
              </Text>
            }
          >
            <Table
              dataSource={results.results.map((id, index) => ({
                id,
                rowId: `${id}-${index}`,
              }))}
              columns={successColumns}
              rowKey="rowId"
              size="small"
              pagination={{ pageSize: 25, showSizeChanger: false }}
            />
          </Card>
        )}

        {results && results.errors.length > 0 && (
          <Card
            size="small"
            title={
              <Text strong>
                <CloseCircleOutlined style={{ color: '#ff4d4f' }} /> Errors (
                {results.errors.length})
              </Text>
            }
          >
            <Table
              dataSource={results.errors.map((error, index) => ({
                ...error,
                rowId: `${error.benefitId}-${index}`,
              }))}
              columns={errorColumns}
              rowKey="rowId"
              size="small"
              pagination={{ pageSize: 25, showSizeChanger: false }}
            />
          </Card>
        )}

        <style>{`
          .ant-pagination-item,
          .ant-pagination-prev,
          .ant-pagination-next {
            margin-inline-end: 6px !important;
          }
        `}</style>
      </Space>
    </Card>
  )
}
